import React, { useState, useRef, useEffect, CSSProperties } from 'react';
import { useLabels, useEditMode } from '../config/labelsContext';

type AllowedElement = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'li' | 'label' | 'div';

interface EditableLabelProps {
  labelKey: string;
  defaultValue: string;
  as?: AllowedElement;
  className?: string;
  style?: CSSProperties;
}

// Get value from nested object by dot path.
function getByPath(obj: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

const EditableLabel: React.FC<EditableLabelProps> = ({
  labelKey,
  defaultValue,
  as: Tag = 'span',
  className,
  style,
}) => {
  const { labels, updateLabel } = useLabels();
  const isEditMode = useEditMode();

  const currentValue =
    getByPath(labels as unknown as Record<string, unknown>, labelKey) ?? defaultValue;
  const isModified = currentValue !== defaultValue;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentValue);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleConfirm = () => {
    setEditing(false);
    if (draft !== currentValue) {
      updateLabel(labelKey, draft);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      setDraft(currentValue);
      setEditing(false);
    }
  };

  if (!isEditMode) {
    return (
      <Tag className={className} style={style}>
        {currentValue}
      </Tag>
    );
  }

  if (editing) {
    const isMultiLine = currentValue.includes('\n') || currentValue.length > 60;
    const inputStyle: CSSProperties = {
      font: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      color: 'inherit',
      background: isModified ? '#fffbeb' : '#fff',
      border: `2px solid ${isModified ? '#f59e0b' : '#3b82f6'}`,
      borderRadius: 3,
      padding: '2px 4px',
      width: '100%',
      boxSizing: 'border-box',
      outline: 'none',
    };

    if (isMultiLine) {
      return (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleConfirm}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          rows={3}
          style={inputStyle}
        />
      );
    }

    return (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleConfirm}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        style={inputStyle}
      />
    );
  }

  // Edit mode but not editing: show text with edit icon
  const editableStyle: CSSProperties = {
    ...style,
    position: 'relative',
    background: isModified ? '#fffbeb' : undefined,
  };

  return (
    <Tag className={className} style={editableStyle}>
      {currentValue}
      <span
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEditing(true);
        }}
        style={{
          display: 'inline-block',
          marginLeft: 4,
          cursor: 'pointer',
          fontSize: '0.7em',
          opacity: 0.5,
          verticalAlign: 'middle',
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.opacity = '0.5';
        }}
        title={`[${labelKey}] Click to edit`}
      >
        ✏️
      </span>
    </Tag>
  );
};

export default EditableLabel;
